/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Comandero F4 — Fidelización y promociones (LÓGICA PURA)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CC-6: este archivo es `.ts` PURO. NO importa macros de lingui ni nada de UI.
 * Recibe números (centavos / puntos / basis points) y devuelve números.
 *
 * Convenciones:
 *   - Montos en enteros de unidades menores (centavos).
 *   - Puntos de fidelización: enteros.
 *   - Promo `percent`: `value` en basis points (1000 = 10%).
 *   - Promo `amount`: `value` en centavos.
 *
 * IMPORTANTE: la regla de acumulación de puntos vive TAMBIÉN en el write-path
 * Rust (`accrue_loyalty_for_order`) que es la fuente de verdad del saldo. Aquí
 * se replica la MISMA tasa para previsualizar en la UI antes del cobro.
 */

/** Centavos del total que valen 1 punto (debe coincidir con LOYALTY_CENTS_PER_POINT en Rust). */
export const LOYALTY_CENTS_PER_POINT = 100;

/** Valor en centavos de descuento de 1 punto al canjear. */
export const LOYALTY_POINT_VALUE_CENTS = 1;

/**
 * Puntos que acumula una orden por su total neto (centavos).
 * 1 punto por cada `LOYALTY_CENTS_PER_POINT` centavos; trunca el resto.
 */
export function pointsEarnedForTotal(netTotalCents: number): number {
  const t = Math.max(0, Math.trunc(netTotalCents));
  return Math.floor(t / LOYALTY_CENTS_PER_POINT);
}

/**
 * Descuento en centavos que aporta canjear `points`, acotado a:
 *   - los puntos disponibles del cliente (`available`), y
 *   - el total a cobrar (`maxCents`) — no se puede descontar más que la cuenta.
 * Devuelve `{ pointsToRedeem, discountCents }` ya consistentes entre sí.
 */
export function redeemPoints(
  pointsRequested: number,
  available: number,
  maxCents: number,
): { pointsToRedeem: number; discountCents: number } {
  const req = Math.max(0, Math.trunc(pointsRequested));
  const have = Math.max(0, Math.trunc(available));
  const cap = Math.max(0, Math.trunc(maxCents));
  // Puntos que como mucho cabe canjear sin pasarse del total.
  const maxByCents = Math.floor(cap / LOYALTY_POINT_VALUE_CENTS);
  const pointsToRedeem = Math.min(req, have, maxByCents);
  return { pointsToRedeem, discountCents: pointsToRedeem * LOYALTY_POINT_VALUE_CENTS };
}

/** Saldo de puntos = suma de movimientos (acumulaciones + canjes con signo). */
export function loyaltyBalance(deltas: number[]): number {
  return deltas.reduce((acc, d) => acc + Math.trunc(d), 0);
}

export type PromotionKind = "percent" | "amount";

export interface PromotionRule {
  kind: PromotionKind;
  /** percent: basis points (1000 = 10%); amount: centavos. */
  value: number;
  /** Happy hour: hora de inicio 0-23 (null/undefined = siempre activa). */
  startHour?: number | null;
  /** Happy hour: hora de fin 0-23 (null/undefined = siempre activa). */
  endHour?: number | null;
  isActive?: boolean;
}

const BASIS_POINTS = 10_000;

/**
 * ¿La promo aplica a la hora `hour` (0-23)?
 * Sin ventana horaria → siempre. La ventana es [start, end): soporta cruce de
 * medianoche (p.ej. 22→2 cubre 22,23,0,1).
 */
export function isPromotionActiveAt(promo: PromotionRule, hour: number): boolean {
  if (promo.isActive === false) {
    return false;
  }
  const h = Math.trunc(hour);
  const start = promo.startHour;
  const end = promo.endHour;
  if (start == null || end == null) {
    return true;
  }
  if (start === end) {
    return true; // ventana de 24h
  }
  if (start < end) {
    return h >= start && h < end;
  }
  // Cruza medianoche.
  return h >= start || h < end;
}

/**
 * Descuento en centavos de una promo sobre `subtotalCents`.
 * `percent` redondea al centavo; `amount` se acota al subtotal. 0 si no aplica.
 */
export function promotionDiscount(
  promo: PromotionRule,
  subtotalCents: number,
  hour: number,
): number {
  if (!isPromotionActiveAt(promo, hour)) {
    return 0;
  }
  const subtotal = Math.max(0, Math.trunc(subtotalCents));
  if (promo.kind === "percent") {
    const raw = Math.round((subtotal * Math.max(0, promo.value)) / BASIS_POINTS);
    return Math.min(raw, subtotal);
  }
  return Math.min(Math.max(0, Math.trunc(promo.value)), subtotal);
}
